import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Plus, X, Check, Loader2, Upload, Image, User, Pencil } from 'lucide-react';
import { toast } from 'sonner';

export default function CreateArtistProfile() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [profileImage, setProfileImage] = useState('');
  const [username, setUsername] = useState('');
  const [originalUsername, setOriginalUsername] = useState('');
  const [uploadingPfp, setUploadingPfp] = useState(false);
  const [formData, setFormData] = useState({
    bio: '',
    genres: [],
    media_urls: [],
    spotify_url: '',
    soundcloud_url: '',
    apple_music_url: '',
    youtube_url: ''
  });
  const [currentGenre, setCurrentGenre] = useState('');
  const [uploading, setUploading] = useState(false);

  // Check if artist profile already exists
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await apiClient.get('/artist/profile');
        const p = response.data;
        setIsEditMode(true);
        setFormData({
          bio: p.bio || '',
          genres: p.genres || [],
          media_urls: p.media_urls || [],
          spotify_url: p.spotify_url || '',
          soundcloud_url: p.soundcloud_url || '',
          apple_music_url: p.apple_music_url || '',
          youtube_url: p.youtube_url || ''
        });
        setProfileImage(p.profile_image || '');
      } catch {
        // No existing profile — stay in create mode
        setIsEditMode(false);
      }
      // Also fetch user info for username/pfp
      try {
        const userResp = await apiClient.get('/auth/me');
        setUsername(userResp.data.username || userResp.data.name || '');
        setOriginalUsername(userResp.data.username || userResp.data.name || '');
        if (!profileImage && userResp.data.profile_image) {
          setProfileImage(userResp.data.profile_image);
        }
      } catch {}
      setLoading(false);
    };
    fetchProfile();
  }, []);

  const addGenre = () => {
    if (currentGenre && !formData.genres.includes(currentGenre)) {
      setFormData({ ...formData, genres: [...formData.genres, currentGenre] });
      setCurrentGenre('');
    }
  };

  const removeGenre = (genre) => {
    setFormData({ ...formData, genres: formData.genres.filter(g => g !== genre) });
  };

  const removeMediaUrl = (url) => {
    setFormData({ ...formData, media_urls: formData.media_urls.filter(u => u !== url) });
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (let file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('folder', 'artist-media');
        const response = await apiClient.post('/upload-file', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setFormData(prev => ({ ...prev, media_urls: [...prev.media_urls, response.data.url] }));
      }
      toast.success('Photos uploaded!');
    } catch (error) {
      toast.error('Failed to upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handlePfpUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPfp(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'profile-images');
      const response = await apiClient.post('/upload-file', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfileImage(response.data.url);
      toast.success('Profile picture updated!');
    } catch {
      toast.error('Failed to upload profile picture');
    } finally {
      setUploadingPfp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        bio: formData.bio,
        genres: formData.genres,
        media_urls: formData.media_urls,
        spotify_url: formData.spotify_url || null,
        soundcloud_url: formData.soundcloud_url || null,
        apple_music_url: formData.apple_music_url || null,
        youtube_url: formData.youtube_url || null
      };

      if (isEditMode) {
        // Update existing profile
        await apiClient.put('/artist/profile', {
          ...payload,
          profile_image: profileImage || null
        });
        toast.success('Artist profile updated!');
      } else {
        // Create new profile
        await apiClient.post('/artists', payload);
        toast.success('Artist profile created! Awaiting admin approval.');
      }

      // Update username if changed
      if (username !== originalUsername && username.trim()) {
        try {
          await apiClient.put('/user/profile', { username: username.trim(), profile_image: profileImage || null });
        } catch {}
      } else if (profileImage) {
        // Just update profile image
        try {
          await apiClient.put('/user/profile', { profile_image: profileImage });
        } catch {}
      }

      navigate('/artist-dashboard');
    } catch (error) {
      toast.error('Failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative pt-20">
      <div className="container mx-auto px-4 py-8 relative z-10 max-w-3xl">
        <Card data-testid="create-artist-form" className="glass-default border-white/10">
          <CardContent className="p-8">
            <h1 className="text-3xl font-bold gradient-text mb-2">
              {isEditMode ? 'Update Artist Profile' : 'Create Artist Profile'}
            </h1>
            <p className="text-white/60 mb-8">
              {isEditMode ? 'Update your profile and save changes' : 'Share your talent with event organizers worldwide'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Profile Picture & Username */}
              <div className="p-5 bg-white/5 rounded-xl border border-white/10 space-y-4" data-testid="profile-section">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <User className="w-4 h-4 text-violet-400" /> Profile
                </h3>
                <div className="flex items-center gap-5">
                  <div className="relative group">
                    <div className="w-20 h-20 rounded-full bg-white/10 border-2 border-white/20 overflow-hidden flex items-center justify-center">
                      {profileImage ? (
                        <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-8 h-8 text-white/30" />
                      )}
                    </div>
                    <input type="file" id="pfp-upload" accept="image/*" onChange={handlePfpUpload} className="hidden" />
                    <button
                      type="button"
                      onClick={() => document.getElementById('pfp-upload').click()}
                      disabled={uploadingPfp}
                      className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-violet-600 hover:bg-violet-500 flex items-center justify-center text-white shadow-lg transition-colors"
                      data-testid="change-pfp-btn"
                    >
                      {uploadingPfp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pencil className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <div className="flex-1 space-y-2">
                    <Label className="text-white/70 text-xs">Username</Label>
                    <Input
                      data-testid="username-input"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Your display name"
                      className="bg-white/5 border-white/10 focus:border-violet-500 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div className="space-y-2">
                <Label htmlFor="bio" className="text-white">Bio / Description *</Label>
                <textarea
                  data-testid="bio-input"
                  id="bio"
                  required
                  rows="4"
                  placeholder="Tell us about your performance style, experience, and what makes you unique..."
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder:text-white/30 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 outline-none"
                />
              </div>

              {/* Genres */}
              <div className="space-y-2">
                <Label className="text-white">Genres / Styles *</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="genre-input"
                    placeholder="e.g., Rock, Jazz, Electronic"
                    value={currentGenre}
                    onChange={(e) => setCurrentGenre(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addGenre())}
                    className="bg-white/5 border-white/10 focus:border-violet-500 text-white"
                  />
                  <Button data-testid="add-genre-button" type="button" onClick={addGenre} className="bg-violet-600 hover:bg-violet-500">
                    <Plus size={18} />
                  </Button>
                </div>
                {formData.genres.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.genres.map((genre, idx) => (
                      <span key={idx} className="px-3 py-1 rounded-full bg-violet-500/20 text-violet-300 flex items-center gap-2">
                        {genre}
                        <button type="button" onClick={() => removeGenre(genre)} className="hover:text-white"><X size={14} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Music Platform Links with checkmarks */}
              <div className="space-y-4 p-4 glass-light rounded-lg">
                <h3 className="font-semibold text-lg text-white">Music Platforms</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'spotify_url', label: 'Spotify', placeholder: 'https://open.spotify.com/artist/...' },
                    { key: 'soundcloud_url', label: 'SoundCloud', placeholder: 'https://soundcloud.com/...' },
                    { key: 'apple_music_url', label: 'Apple Music', placeholder: 'https://music.apple.com/...' },
                    { key: 'youtube_url', label: 'YouTube', placeholder: 'https://youtube.com/@...' },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key} className="space-y-2">
                      <Label className="text-white flex items-center gap-2">
                        {label}
                        {formData[key] && (
                          <span className="inline-flex items-center gap-0.5 text-xs text-emerald-400" data-testid={`check-${key}`}>
                            <Check size={12} /> Saved
                          </span>
                        )}
                      </Label>
                      <Input
                        data-testid={`${key.replace('_url', '')}-url-input`}
                        placeholder={placeholder}
                        value={formData[key]}
                        onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                        className={`bg-white/5 border-white/10 focus:border-violet-500 text-white ${formData[key] ? 'border-emerald-500/30' : ''}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Media Gallery */}
              <div className="space-y-3">
                <Label className="text-white">Gallery Photos</Label>
                <div className="flex gap-2">
                  <input type="file" id="file-upload" multiple accept="image/*" onChange={handleFileUpload} className="hidden" disabled={uploading} />
                  <Button
                    data-testid="upload-file-button" type="button"
                    onClick={() => document.getElementById('file-upload').click()}
                    disabled={uploading}
                    className="bg-fuchsia-600 hover:bg-fuchsia-500 flex-1"
                  >
                    {uploading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload from Device</>}
                  </Button>
                </div>
                {formData.media_urls.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                    {formData.media_urls.map((url, idx) => (
                      <div key={idx} className="relative group aspect-video rounded-lg overflow-hidden bg-white/5 border border-white/10">
                        <img src={url} alt={`Media ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                        <button
                          type="button" onClick={() => removeMediaUrl(url)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-media-${idx}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {formData.media_urls.length === 0 && (
                  <div className="text-center py-6 border border-dashed border-white/10 rounded-lg">
                    <Image className="w-6 h-6 mx-auto mb-2 text-white/20" />
                    <p className="text-white/30 text-sm">No photos yet. Upload from your device!</p>
                  </div>
                )}
              </div>

              <Button
                data-testid="submit-button" type="submit"
                disabled={submitting || formData.genres.length === 0}
                className="w-full h-14 text-lg font-bold rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 shadow-[0_0_20px_rgba(139,92,246,0.4)] hover:shadow-[0_0_30px_rgba(139,92,246,0.6)] transition-all"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Saving...</>
                ) : isEditMode ? 'Save Changes' : 'Create Artist Profile'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
